import React, { useContext, useEffect, useState } from "react";
import { QuotationSchema, QuotationUpdateSchema } from "../types";
import { Container, PageHeading } from "@ui-components/Container";
import { FormattedMessage, useIntl } from "react-intl";
import { WorksiteInfo } from "../worksite-info/worksite-info";
import CountryContext from "../../../CountryContext";
import { QuotationDetailProps } from "../types";
import { QuotationDraftsTable } from "../quotation-drafts-table/quotation-drafts-table";
import { useQuotations } from "../hooks/useQuotations";
import { QuotationAttachmentsTable } from "../quotation-attachments-table";
import { QuotationState } from "./components/quotation-state";
import { getQuotationUsers } from "./components/quotation-state/lib/utilities";
import AuthenticationService from "@services/AuthenticationService";
import Button from "@ui-components/Button";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useHistory } from "react-router-dom";
import { ResaleFirstDraftCreationModal } from "../ResaleFirstDraftCreationModal";
import { inviteExternalEnterprise } from "../lib/utilities";
import { NotificationsContext } from "../../../ui-components/Notifications";
import { SendToEnterpriseModal } from "../sent-to-enterprise-modal";
import { EstimeDatesModal } from "@feature/worksites/estime-dates/estime-dates-modal";

export const QuotationDetail = ({
  quotation,
  setQuotation,
}: QuotationDetailProps) => {
  // hooks
  const intl = useIntl();
  const {
    fetchOne,
    updateQuotation,
    invite,
    fetchBaseDraftInfo,
    loadingInvitation,
    sendToEnterprise,
    sendingToEnterprise,
  } = useQuotations();
  const {
    country: { code },
  } = useContext(CountryContext);
  const history = useHistory();
  const { push } = useContext(NotificationsContext);

  // state
  const [baseDraftInfo, setBaseDraftInfo] = useState<{
    id: number | null;
    message: string;
  }>();
  const [
    showResaleFirstDraftCreationModal,
    setShowResaleFirstDraftCreationModal,
  ] = useState(false);
  const [showSendToEnterpriseModal, setShowSendToEnterpriseModal] =
    useState(false);
  const [showEstimeDates, setShowEstimeDates] = useState(false);
  const [submittingSendToEnterprise, setSubmittingSendToEnterprise] =
    useState(false);

  // utilities
  const userScopes = AuthenticationService.getUserScopes();
  const userData = AuthenticationService.getUserData();
  const baseDraftNumber = quotation.drafts?.find(
    (d) => d.id === baseDraftInfo?.id
  )?.number;
  const latestResaleDraft = quotation.drafts?.find(
    (d) => d.id_creator === quotation.id_resale && d.status === "latest"
  );
  const disabledBecauseOfRoleAndStatus =
    quotation.status == "waiting_for_agent" &&
    userData.id === quotation.id_resale;

  const canDraftBeSent =
    // User has the scope to send the quotation to the enterprise...
    userScopes.includes("quotation:send_to_enterprise") &&
    // ...and the enterprise has been set and invited
    !!quotation?.id_enterprise &&
    // ...and the quotation is in a state where it can be sent to the enterprise, which means
    // that the quotation is waiting for resale and that there is at least a draft created by the resale
    quotation.status === "waiting_for_resale" &&
    !!latestResaleDraft &&
    quotation?.r_perm.indexOf(`user_${quotation.id_enterprise}`) >= 0;

  useEffect(() => {
    if (!userScopes.includes("quotation:create_draft")) return;
    fetchBaseDraftInfo(quotation.id).then(setBaseDraftInfo);
  }, []);

  const sendToEnterpriseFunc = async () => {
    setSubmittingSendToEnterprise(true);
    try {
      let draftId;
      if (!baseDraftInfo?.id) {
        /* this means that the user is the agent but the latest final draft has
              been created by the resale, the agent can't create a new final draft and hence
              has no baseDraftInfo.id; so we send the latest final draft of the resale to the enterprise,
              but we need to look up the id of the latest final draft of the resale
               */
        draftId = latestResaleDraft?.id;
      } else {
        draftId = baseDraftInfo?.id;
      }
      if (!draftId) {
        push({
          type: "error",
          title: intl.formatMessage({ id: "an_error_occurred" }),
        });
        setSubmittingSendToEnterprise(false);
        return;
      }
      await sendToEnterprise(draftId);
      const updated = await fetchOne(quotation.id);
      setQuotation(updated);
      const newBaseDraftInfo = await fetchBaseDraftInfo(quotation.id);
      setBaseDraftInfo(newBaseDraftInfo);
    } catch (e) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
    } finally {
      setShowSendToEnterpriseModal(false);
    }
    setSubmittingSendToEnterprise(false);
  };

  return (
    <>
      <Container>
        <div className="flex flex-col gap-5 mb-10">
          <PageHeading
            title={`${intl.formatMessage({ id: "quotation_long" })} / n° ${
              quotation.id
            }`}
            titleClasses={"text-xl"}
            // @ts-ignore
            historyBackPath={`/${code}/quotations`}
          />
          {quotation.worksites && (
            <WorksiteInfo
              worksite={quotation?.worksites}
              quotation={quotation as QuotationSchema} // drafts are useless here
            />
          )}
        </div>
        <div className="grid gap-y-10 mb-10">
          <QuotationState
            editable={
              userScopes.includes("quotation:update") &&
              quotation.status !== "archived" &&
              userData.role_type !== "enterprise"
            }
            worksite={quotation.worksites}
            status={quotation.status}
            users={getQuotationUsers(quotation)}
            onUserChange={async (key, userSelected) => {
              if (
                !!userSelected &&
                key === "enterprise" &&
                userSelected?.object === null
              ) {
                await inviteExternalEnterprise(
                  userSelected,
                  quotation,
                  updateQuotation,
                  code,
                  push,
                  intl,
                  setQuotation
                );
              } else {
                const updated = await updateQuotation(quotation.id, {
                  ...quotation,
                  [`id_${key}` as keyof QuotationUpdateSchema]:
                    userSelected?.object?.account_user_id || userSelected.value || null,
                  [`des_${key}` as keyof QuotationUpdateSchema]:
                    userSelected.label || null,
                });
                setQuotation(updated);
              }
            }}
            submittingUserChange={loadingInvitation}
            onUserInvite={async (key) => {
              const updated = await invite(quotation.id, key);
              setQuotation(updated);
            }}
            submittingUserInvite={loadingInvitation}
          />
          {!!baseDraftInfo &&
            !["approved", "archived", "undefined"].includes(
              quotation.status
            ) && (
              <div className="flex flex-row gap-4 items-center">
                {userScopes.includes("quotation:send_to_enterprise") && (
                  <Button
                    onClick={() => {
                      if (!canDraftBeSent) return;
                      setShowSendToEnterpriseModal(true);
                    }}
                    disabled={!canDraftBeSent}
                    containerClassName="w-full"
                    submitting={submittingSendToEnterprise}
                  >
                    <PaperAirplaneIcon className="w-5 mr-1" />
                    <span className="whitespace-nowrap">
                      <FormattedMessage
                        id="send_to_enterprise"
                        defaultMessage="Invia all'impresa"
                      />
                    </span>
                  </Button>
                )}
              </div>
            )}
          <QuotationDraftsTable
            quotation={quotation}
            baseDraftInfo={baseDraftInfo}
          />
          <QuotationAttachmentsTable quotation={quotation} />
        </div>
      </Container>
      {showResaleFirstDraftCreationModal && (
        <ResaleFirstDraftCreationModal
          onConfirm={() => {
            setShowResaleFirstDraftCreationModal(false);
            history.push(
              `/${code}/quotations/${quotation.id}/drafts/${baseDraftInfo?.id}`
            );
          }}
          onDismiss={() => setShowResaleFirstDraftCreationModal(false)}
        />
      )}
      {showSendToEnterpriseModal &&
        quotation?.des_enterprise &&
        (!!baseDraftNumber || !!latestResaleDraft) && (
          <SendToEnterpriseModal
            onDismiss={() => {
              setShowSendToEnterpriseModal(false);
            }}
            onConfirm={async () => {
              // Se le date di stima mancano apri il modale
              if (
                quotation.worksites.estime_date_end === null ||
                quotation.worksites.estime_date_start === null
              ) {
                setShowSendToEnterpriseModal(false);
                setShowEstimeDates(true);
                return;
              }
              // Se ci sono le date di stima procedi con l'invio
              await sendToEnterpriseFunc();
            }}
            sending={sendingToEnterprise}
            des_enterprise={quotation?.des_enterprise}
            baseDraftNumber={baseDraftNumber || latestResaleDraft?.number}
          />
        )}
      {showEstimeDates ? (
        <EstimeDatesModal
          setOpenModal={setShowEstimeDates}
          selectedWorksite={quotation.worksites}
          setSelectedWorksite={sendToEnterpriseFunc}
          text={intl.formatMessage({
            id: "missing_estime_dates_quotation_needed",
            defaultMessage:
              "Per procedere con l'invio, inserisci le date stimate di inizio e fine dell'esecuzione del cantiere.",
          })}
        />
      ) : null}
    </>
  );
};
